<!--
  Blueprint Item Data Tree Component Template

  This template renders a single blueprint item and its children in a tree structure.
  There are three types of items:
  1. Regular editable items (no blueprint_id)
  2. Blueprint link items, that are real items with a blueprint_id that link in other blueprints. These aren't editable,
  and their children are generated on-the-fly based on the linked blueprint's items.
  2. Blueprint reference items (ref-* items, read-only view), that are generated on the fly from blueprint link items.

  The component supports drag-and-drop for editable items and recursive rendering of nested children.
-->
@if (pageData$ | async; as pageData) {
  <form [formGroup]="form">
    <!-- Main Item Row: Grid layout with conditional styling based on expansion state -->
    <div
      class="border border-surface-switch-300 items-center bg-surface-switch-50"
      [class.ms-4]="depth > 0"
      [class.bg-surface-switch-100]="expanded"
    >
      <div
        class="grid gap-2"
        [ngClass]="
          tradeStudyToolFeaturesEnabled
            ? 'grid-cols-[25px_1.2fr_75px_0.5fr_55px_1.3fr_80px_1.8fr_0.9fr_110px]'
            : 'grid-cols-[25px_1.2fr_75px_0.5fr_55px_1.3fr_80px_70px_0.9fr_1fr_110px]'
        "
      >
        @if (!isBlueprintReferenceItem) {
          <div
            pDraggable
            (onDragStart)="blueprintItemDataEditorService.dragStart(item)"
            (onDragEnd)="blueprintItemDataEditorService.dragEnd()"
            class="flex justify-center items-center px-4 cursor-grab hover:bg-primary-100"
          >
            <i class="pi pi-ellipsis-v"></i>
          </div>
        } @else {
          <div></div>
        }
        <div
          class="grid grid-cols-subgrid items-center"
          [class.col-span-10]="!tradeStudyToolFeaturesEnabled"
          [class.col-span-9]="tradeStudyToolFeaturesEnabled"
          [class.dragging-item]="
            pageData.dragging &&
            pageData.dragging.persistent_id == item.persistent_id
          "
        >
          <!-- Name (1.2fr) -->
          <div
            class="font-semibold flex items-center gap-2 overflow-hidden min-w-0"
          >
            @if (isBlueprintLinkItem) {
              <div
                class="blueprint-link-name-stack flex w-full min-w-0 flex-col gap-1 self-start py-0.5"
              >
                <span
                  class="text-xs font-normal leading-snug text-muted-color [overflow-wrap:anywhere] break-words px-0.5"
                  >Blueprint: {{ blueprintName }}</span
                >
                <input
                  pInputText
                  [id]="'blueprint-link-item-name-' + item.persistent_id"
                  type="text"
                  formControlName="name"
                  class="w-full min-w-0 border-0 bg-transparent px-1 py-0.5 font-semibold"
                  [placeholder]="'Item ' + ((item.sibling_order || 0) + 1)"
                />
              </div>
            } @else {
              <input
                pInputText
                type="text"
                formControlName="name"
                class="w-full border-0 bg-transparent p-0 font-semibold"
                [placeholder]="'Item ' + ((item.sibling_order || 0) + 1)"
              />
            }
          </div>
          <!-- Quantity (75px) -->
          <div class="flex items-center text-muted-color">
            @if (isBlueprintLinkItem) {
              <span class="text-xs text-muted-color">—</span>
            } @else if (selectedPricingModelHasOutputQuantity) {
              <!-- Quantity is calculated by pricing model when output_quantity is enabled -->
              <span
                class="text-sm text-muted-color italic"
                pTooltip="Quantity will be calculated by the pricing model"
                tooltipPosition="top"
                >Auto</span
              >
            } @else {
              <input
                pInputText
                type="text"
                formControlName="quantity"
                class="w-full border-0 bg-transparent p-0 text-xs"
                [class.text-muted-color]="!form.get('quantity')?.value"
                [placeholder]="
                  form.get('quantity_type')?.value == QuantityType.HOURLY
                    ? 'hh:mm:ss'
                    : 'Qty'
                "
                [invalid]="
                  form.get('quantity')?.invalid && form.get('quantity')?.touched
                "
              />
            }
          </div>
          <!-- Quantity Type (0.5fr) -->
          <div
            class="flex items-center text-muted-color overflow-hidden min-w-0"
          >
            @if (children.length > 0 || isBlueprintLinkItem) {
              <span class="text-xs text-muted-color">—</span>
            } @else if (!selectedPricingModelHasOutputQuantity) {
              <p-select
                formControlName="quantity_type"
                [options]="quantityTypeOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="—"
                class="w-full"
                [style]="{ 'font-size': '0.75rem' }"
                appendTo="body"
              />
            } @else {
              <span class="text-sm text-muted-color italic">Auto</span>
            }
          </div>
          <!-- Batch Size (55px) -->
          <div class="flex items-center text-muted-color">
            @if (isBlueprintLinkItem) {
              <span class="text-xs text-muted-color">—</span>
            } @else {
              <p-inputNumber
                formControlName="batch_size"
                [inputStyle]="{ width: '100%', textAlign: 'left' }"
                [min]="0"
                [max]="999999"
                [maxFractionDigits]="0"
                [minFractionDigits]="0"
                placeholder="—"
              />
            }
          </div>
          <!-- Pricing Model (1.3fr) -->
          <div
            class="flex items-center text-muted-color overflow-hidden min-w-0"
          >
            @if (children.length > 0 || isBlueprintLinkItem) {
              <span class="text-xs text-muted-color">—</span>
            } @else {
              <p-select
                formControlName="pricing_model_id"
                [options]="pricingModelService.selectOptions()"
                optionLabel="name"
                optionValue="id"
                placeholder="Manual"
                class="w-full text-xs"
                [style]="{ 'font-size': '0.75rem' }"
                appendTo="body"
                [filter]="true"
                filterBy="name"
              />
            }
          </div>
          <!-- Unit Cost (80px) -->
          <div class="flex items-center text-muted-color">
            @if (children.length > 0 || isBlueprintLinkItem) {
              <span class="text-xs text-muted-color">—</span>
            } @else if (
              isManualPricing || !selectedPricingModelHasOutputUnitCost
            ) {
              <!-- User can set unit_cost when:
                   - Manual pricing is selected
                   - Pricing model does not output unit_cost
              -->
              <p-inputNumber
                formControlName="unit_cost"
                [inputStyle]="{ width: '100%', textALign: 'left' }"
                [min]="0"
                [max]="9999999"
                [mode]="'currency'"
                [currency]="currencyCode || 'GBP'"
                locale="en-US"
              />
            } @else {
              <span class="text-sm text-muted-color italic">Auto</span>
            }
          </div>
          @if (!tradeStudyToolFeaturesEnabled) {
            <!-- Markup Percentage (70px) -->
            <div class="flex items-center text-muted-color">
              @if (children.length > 0 || isBlueprintLinkItem) {
                <span class="text-xs text-muted-color">—</span>
              } @else {
                <p-inputNumber
                  formControlName="markup_percentage"
                  mode="decimal"
                  [inputStyle]="{ width: '100%', textAlign: 'left' }"
                  [min]="-999"
                  [max]="999"
                  [minFractionDigits]="0"
                  [maxFractionDigits]="2"
                >
                </p-inputNumber>
              }
            </div>
            <!-- Tax Code (0.9fr) -->
            <div
              class="flex items-center text-muted-color overflow-hidden min-w-0"
            >
              @if (children.length > 0 || item.blueprint_id) {
                <span class="text-xs text-muted-color">—</span>
              } @else {
                <app-tax-code-select
                  formControlName="tax_code_id"
                  [isNewItem]="isNewItem"
                  class="w-full"
                />
              }
            </div>
          }
          <!-- Material (1fr) -->
          <div
            class="flex items-center text-muted-color overflow-hidden min-w-0"
          >
            <app-material-select formControlName="material_id" class="w-full" />
          </div>
          @if (tradeStudyToolFeaturesEnabled) {
            <!-- CapEx Equipment (0.9fr) -->
            <div
              class="flex items-center text-muted-color overflow-hidden min-w-0"
            >
              @if (children.length > 0 || isBlueprintLinkItem) {
                <span class="text-xs text-muted-color">—</span>
              } @else {
                <p-multiSelect
                  formControlName="capex_equipment_ids"
                  [options]="equipmentOptions"
                  optionLabel="name"
                  optionValue="id"
                  placeholder="Select"
                  display="chip"
                  class="w-full"
                  appendTo="body"
                  [maxSelectedLabels]="1"
                  [selectedItemsLabel]="'{0} selected'"
                  [pTooltip]="capexTooltip"
                  tooltipPosition="top"
                />
              }
            </div>
          }
          <!-- Actions (110px) -->
          <div class="flex justify-end">
            @if (!isBlueprintReferenceItem) {
              <p-button-group>
                @if (children.length > 0 || isBlueprintLinkItem) {
                  <p-button
                    text="true"
                    severity="secondary"
                    [icon]="
                      expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-left'
                    "
                    (onClick)="onToggleExpanded()"
                  />
                }
                @if (!isBlueprintLinkItem) {
                  <p-button
                    text="true"
                    severity="secondary"
                    icon="pi pi-plus"
                    (onClick)="onAddChildToThisItem(item.persistent_id)"
                    [pTooltip]="'Add Child Item'"
                    tooltipPosition="top"
                  />
                }
              </p-button-group>
              @if (
                consumablesEnabled &&
                children.length === 0 &&
                !isBlueprintLinkItem
              ) {
                <app-item-consumables-editor
                  [catalog]="consumableOptions"
                  [selected]="item.consumables ?? []"
                  [currencyCode]="currencyCode || 'GBP'"
                  (selectedChange)="onConsumablesChange($event)"
                />
              }
              <p-button
                text="true"
                severity="danger"
                icon="pi pi-trash"
                (onClick)="onRemoveItem()"
                [pTooltip]="'Remove Item'"
                tooltipPosition="top"
              />
            } @else {
              @if (children.length > 0 || isBlueprintLinkItem) {
                <p-button
                  text="true"
                  severity="secondary"
                  [icon]="
                    expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-left'
                  "
                  (onClick)="onToggleExpanded()"
                />
              }
            }
          </div>
        </div>
      </div>
      @if (expanded) {
        @if (
          isBlueprintLinkItem &&
          !isBlueprintReferenceItem &&
          referencedBlueprint
        ) {
          <!-- Read-only mode for blueprint link items: Show blueprint items generated on-the-fly -->
          <div
            class="ml-4"
            [class.col-span-10]="!tradeStudyToolFeaturesEnabled"
            [class.col-span-9]="tradeStudyToolFeaturesEnabled"
          >
            @for (
              refChild of topLevelReferencedBlueprintChildren;
              track refChild.persistent_id
            ) {
              <app-blueprint-item-data-tree
                [item]="refChild"
                [allItems]="referencedBlueprintChildren"
                [allBlueprints]="allBlueprints"
                [depth]="depth + 1"
                [currencyCode]="currencyCode || 'GBP'"
                [tradeStudyToolFeaturesEnabled]="tradeStudyToolFeaturesEnabled"
                [equipmentOptions]="equipmentOptions"
                [consumableOptions]="consumableOptions"
                [consumablesEnabled]="consumablesEnabled"
                (addChild)="onAddChildToThisItem($event)"
                (removeItem)="removeItem.emit($event)"
              />
            }
          </div>
        }
        @if (
          isBlueprintReferenceItem && isBlueprintLinkItem && referencedBlueprint
        ) {
          <!-- Read-only mode for ref-* items: Show referenced blueprint items children -->
          <div
            class="ml-4"
            [class.col-span-10]="!tradeStudyToolFeaturesEnabled"
            [class.col-span-9]="tradeStudyToolFeaturesEnabled"
          >
            @for (child of children; track child.persistent_id) {
              <app-blueprint-item-data-tree
                [item]="child"
                [allItems]="referencedBlueprintItems"
                [allBlueprints]="allBlueprints"
                [depth]="depth + 1"
                [currencyCode]="currencyCode || 'GBP'"
                [tradeStudyToolFeaturesEnabled]="tradeStudyToolFeaturesEnabled"
                [equipmentOptions]="equipmentOptions"
                [consumableOptions]="consumableOptions"
                [consumablesEnabled]="consumablesEnabled"
                (addChild)="onAddChildToThisItem($event)"
                (removeItem)="removeItem.emit($event)"
              />
            }
          </div>
        }
        @if (
          !isBlueprintLinkItem ||
          (isBlueprintReferenceItem && !referencedBlueprint)
        ) {
          <!-- Drag and drop -->
          <div
            class="flex flex-col gap-1 pt-1 pb-2"
            [class.col-span-10]="!tradeStudyToolFeaturesEnabled"
            [class.col-span-9]="tradeStudyToolFeaturesEnabled"
            [class.dragging-active]="pageData.dragging"
          >
            @for (child of children; track child.persistent_id) {
              <div
                pDroppable
                (drop)="blueprintItemDataEditorService.drop(child, 'before')"
                class="drop-line ml-4"
                [class.hidden]="isBlueprintReferenceItem"
              ></div>
              <div>
                <app-blueprint-item-data-tree
                  [item]="child"
                  [allItems]="allItems"
                  [allBlueprints]="allBlueprints"
                  [depth]="depth + 1"
                  [currencyCode]="currencyCode || 'GBP'"
                  [tradeStudyToolFeaturesEnabled]="
                    tradeStudyToolFeaturesEnabled
                  "
                  [equipmentOptions]="equipmentOptions"
                  [consumableOptions]="consumableOptions"
                  [consumablesEnabled]="consumablesEnabled"
                  (addChild)="onAddChildToThisItem($event)"
                  (removeItem)="removeItem.emit($event)"
                />
              </div>
              @if ($last) {
                <div
                  pDroppable
                  (drop)="
                    blueprintItemDataEditorService.drop(child, 'endOfList')
                  "
                  class="drop-line ml-4"
                  [class.hidden]="isBlueprintReferenceItem"
                ></div>
              }
            } @empty {
              <div
                pDroppable
                (drop)="blueprintItemDataEditorService.drop(item, 'child')"
                class="drop-line"
                [class.hidden]="isBlueprintReferenceItem"
              ></div>
              <div class="text-muted-color text-sm italic px-4 py-2">
                No child items
              </div>
            }
          </div>
        }
      }
    </div>
  </form>
}
